// TypeScript types mirroring the Rust core public API.
// These are the canonical types used by all TS platform wrappers.

export type Environment = 'development' | 'staging' | 'production';

export type Platform = 'ios' | 'android' | 'react-native' | 'web' | 'node' | 'flutter' | 'macos';

export interface LayersCoreConfig {
  appId: string;
  baseUrl?: string;
  /**
   * Host for ATT-authorized iOS event batches that carry the IDFA.
   * Defaults to https://att.layers.com independently of baseUrl.
   * Set both URLs for a mock server or proxy. Web and Node never send to it.
   */
  trackingBaseUrl?: string;
  environment: Environment;
  flushIntervalMs?: number;
  flushThreshold?: number;
  maxQueueSize?: number;
  maxBatchSize?: number;
  /** Verbose console logging on the JS side (does NOT enable DebugView). */
  enableDebug?: boolean;
  /**
   * Enable per-device DebugView. When true, the SDK generates and persists a
   * stable `debug_token` (UUID) and sends it in the `X-Debug-Token` header on
   * every request to `/events`. Server-side routing of debug-tagged events to
   * the live-tail UI is out of scope here — this flag just plumbs the header.
   */
  debug?: boolean;
  sdkVersion?: string;

  // ── Tier 9 (privacy posture) ────────────────────────────────────────
  /**
   * Web only — when `true`, the SDK respects the browser's Do-Not-Track
   * signal (`navigator.doNotTrack`, `navigator.msDoNotTrack`,
   * `window.doNotTrack`). When DNT is active, analytics flush is gated as
   * if `analyticsStorage = false`.
   */
  respectDnt?: boolean;
  /**
   * Web only — cookieless identification mode. Tells the SDK NOT to use
   * localStorage/sessionStorage for identity persistence; the server
   * stitches by hashed `(IP + UA + appId)` instead. The SDK announces this
   * to the ingest service via an `X-Cookieless: true` request header.
   */
  cookielessMode?: boolean;
  /**
   * Opt-in-by-default mode. When `true`, the SDK queues events but does
   * NOT flush until the host app makes its first explicit `setConsent`
   * call granting `analyticsStorage`.
   */
  consentRequired?: boolean;
  /**
   * Tier 4: optional bootstrap data for feature flags. Lets SSR / pre-render
   * layers seed flag values + payloads at SDK init so the first render doesn't
   * flicker from default-off to actual-value while the first /config is in
   * flight.
   *
   * Accepts either the canonical snake_case shape (round-trips with the wire
   * protocol) or the camelCase shape preferred by JS / TS callers — the
   * wrapper normalizes both before handing off to Rust.
   */
  bootstrap?: FeatureFlagBootstrap | FeatureFlagBootstrapData;
}

/**
 * Tier 4: feature flag value type.
 *
 * - `boolean` for binary flags
 * - `string` for multivariate flags (the variant key)
 */
export type FeatureFlagValue = boolean | string;

/**
 * Tier 4: SSR bootstrap payload. The keys mirror the JSON shape sent over the
 * wire (snake_case) so the same blob can round-trip from server to client
 * without a re-mapping layer.
 */
export interface FeatureFlagBootstrapData {
  feature_flags?: Record<string, FeatureFlagValue>;
  feature_flag_payloads?: Record<string, unknown>;
}

/**
 * Tier 4: SSR bootstrap payload — camelCase-friendly variant.
 *
 * Surface-API counterpart to {@link FeatureFlagBootstrapData}. JS / TS
 * callers commonly construct this from camelCase config objects (Next.js
 * `getServerSideProps`, Remix loaders, etc.); the wrapper accepts either
 * shape and normalizes to snake_case before handing the payload to Rust.
 */
export interface FeatureFlagBootstrap {
  featureFlags?: Record<string, FeatureFlagValue>;
  featureFlagPayloads?: Record<string, unknown>;
}

/**
 * Listener invoked whenever feature flag definitions are refreshed.
 *
 * The callback receives the snapshot of currently-known flag keys after
 * the refresh has completed. Use it to trigger a re-render of any UI that
 * depends on flag values.
 *
 * Implementations MUST NOT throw — exceptions are swallowed and logged.
 */
export type FeatureFlagsListener = (knownFlagKeys: readonly string[]) => void;

/**
 * Tier 4: discriminated-union of feature flag conditions. Mirrors the
 * server-side schema; unknown variants are rejected by the Rust core's
 * deserializer.
 */
export type FeatureFlagCondition =
  | { type: 'property_equals'; property: string; value: unknown }
  | { type: 'property_in'; property: string; values: unknown[] }
  | { type: 'property_contains'; property: string; value: string }
  | { type: 'property_regex'; property: string; pattern: string }
  | { type: 'property_greater_than'; property: string; value: number }
  | { type: 'property_less_than'; property: string; value: number }
  | { type: 'percentage_rollout'; percentage: number }
  | { type: 'cohort'; cohort_id: string };

export interface FeatureFlagVariant {
  key: string;
  weight: number;
}

export interface FeatureFlagDefinition {
  /** Optional inline key — usually overridden by the parent map key. */
  key?: string;
  /** Defaults to true when omitted. */
  active?: boolean;
  conditions?: FeatureFlagCondition[];
  variants?: FeatureFlagVariant[];
  payload?: unknown;
}

/**
 * Snapshot of an event passed to the `before_send` hook.
 *
 * The hook receives the event name, properties bag, and the user/anonymous
 * identifiers attached to the call. Returning `null` drops the event.
 * Returning a (possibly modified) `BeforeSendEvent` forwards the modified
 * event to the queue.
 *
 * Hooks must be synchronous (no async / Promise). They run before queue
 * insertion so any expensive work blocks the call.
 */
export interface BeforeSendEvent {
  /** The event name (e.g. "purchase_success"). */
  event: string;
  /** Event properties (mutable copy — hook may mutate or return a new object). */
  properties: Record<string, unknown>;
  /** Optional app user id passed to track()/screen(). */
  userId?: string;
  /** Optional anonymous id passed to track()/screen(). */
  anonymousId?: string;
}

/**
 * Synchronous hook applied to every event before it reaches the Rust queue.
 *
 * - Return `null` to drop the event silently.
 * - Return a (possibly modified) `BeforeSendEvent` to forward it.
 * - Throwing inside the hook drops the event and emits a debug warning.
 */
export type BeforeSendHook = (event: BeforeSendEvent) => BeforeSendEvent | null | undefined;

/**
 * E-commerce line item used by the typed `trackPurchase`, `trackAddToCart`,
 * `trackBeginCheckout`, and `trackRefund` helpers. Mirrors the GA4 / Meta
 * pixel schema so the same payload can fan out to multiple destinations.
 */
export interface EcommerceItem {
  /** SKU / product identifier. */
  itemId: string;
  /** Display name. */
  itemName?: string;
  /** Quantity purchased / added. Defaults to 1. */
  quantity?: number;
  /** Per-unit price in `currency`. */
  price?: number;
  /** ISO-4217 currency code (e.g. "USD"). */
  currency?: string;
  /** Top-level category. */
  category?: string;
  /** Optional brand. */
  brand?: string;
  /** Optional variant. */
  variant?: string;
  /** Optional discount applied. */
  discount?: number;
  /** Optional coupon code. */
  coupon?: string;
}

/**
 * Snapshot of all multi-group memberships set via `setGroup` / `addGroup`.
 * Keys are group_type (e.g. "company", "team"), values are the group_id.
 */
export type GroupsState = Record<string, string>;

export interface DeviceContext {
  platform?: Platform;
  osVersion?: string;
  appVersion?: string;
  deviceModel?: string;
  locale?: string;
  buildNumber?: string;
  screenSize?: string;
  installId?: string;
  idfa?: string;
  idfv?: string;
  attStatus?: string;
  deeplinkId?: string;
  timezone?: string;
}

/**
 * Consent state across the four Firebase Consent Mode v2 categories.
 *
 * All fields default to `null` (treated as Denied — opt-in posture). The
 * `analytics` / `advertising` fields are deprecated aliases retained for
 * backwards compat with pre–Tier-9 callers; new code should use the
 * explicit v2 fields.
 */
export interface ConsentState {
  /** Gates analytics tracking. Default: Granted unless explicitly denied. */
  analyticsStorage?: boolean | null;
  /** Gates ad-related identifiers (IDFA, click IDs). Default: Granted unless explicitly denied. */
  adStorage?: boolean | null;
  /** Gates user data forwarded to ad partners. Default: Granted unless explicitly denied. */
  adUserData?: boolean | null;
  /** Gates personalized advertising. Default: Granted unless explicitly denied. */
  adPersonalization?: boolean | null;

  /** @deprecated alias for `analyticsStorage` */
  analytics?: boolean | null;
  /** @deprecated alias for `adStorage` */
  advertising?: boolean | null;
}

/**
 * Event properties attached to track/screen calls.
 *
 * Supported value types:
 * - `string` — text values (max 64 KB per value)
 * - `number` — integers and floating-point numbers
 * - `boolean` — true/false flags
 * - `null` — explicit null to clear a property
 * - Nested `Record<string, unknown>` objects (one level deep recommended)
 *
 * Keys must be plain strings (max 128 chars). Prototype-pollution keys
 * (`__proto__`, `constructor`, `prototype`) are rejected. Maximum 100 keys
 * per call, 1 MB total payload.
 */
export interface EventProperties {
  [key: string]: unknown;
}

/**
 * User properties set via `setUserProperties()`.
 *
 * Supported value types:
 * - `string` — text values (max 64 KB per value)
 * - `number` — integers and floating-point numbers
 * - `boolean` — true/false flags
 * - `null` — explicit null to clear a property
 * - Nested `Record<string, unknown>` objects (one level deep recommended)
 *
 * Keys must be plain strings (max 128 chars). Prototype-pollution keys
 * (`__proto__`, `constructor`, `prototype`) are rejected. Maximum 100 keys
 * per call.
 */
export interface UserProperties {
  [key: string]: unknown;
}

export interface HttpResponse {
  status: number;
  body: Uint8Array;
  headers: Array<[string, string]>;
}

export interface HttpClient {
  post(url: string, headers: Array<[string, string]>, body: Uint8Array): Promise<HttpResponse>;
  get(url: string, headers: Array<[string, string]>): Promise<HttpResponse>;
}

export interface PersistenceBackend {
  write(key: string, data: Uint8Array): void;
  read(key: string): Uint8Array | null;
  delete(key: string): void;
  listKeys(prefix: string): string[];
}

export type LayersErrorCode =
  | 'NOT_INITIALIZED'
  | 'ALREADY_INITIALIZED'
  | 'SHUT_DOWN'
  | 'INVALID_CONFIG'
  | 'INVALID_ARGUMENT'
  | 'QUEUE_FULL'
  | 'SERIALIZATION'
  | 'NETWORK'
  | 'HTTP'
  | 'CIRCUIT_OPEN'
  | 'RATE_LIMITED'
  | 'CONSENT_NOT_GRANTED'
  | 'PERSISTENCE'
  | 'REMOTE_CONFIG'
  | 'EVENT_DENIED'
  | 'INTERNAL';

export class LayersError extends Error {
  readonly code: LayersErrorCode;
  readonly status?: number;

  constructor(code: LayersErrorCode, message: string, status?: number) {
    super(message);
    this.name = 'LayersError';
    this.code = code;
    if (status != null) {
      this.status = status;
    }
  }
}

// ── Tier 8: Surveys ───────────────────────────────────────────────────

export type SurveyType =
  | 'nps'
  | 'csat'
  | 'ces'
  | 'multiple_choice'
  | 'rating'
  | 'open_text'
  | 'link';

export type SurveyPosition = 'bottom-right' | 'bottom-center' | 'center' | 'top-center';

export interface SurveyQuestion {
  id: string;
  text: string;
  type: SurveyType;
  options?: string[];
  scale_min?: number;
  scale_max?: number;
  required?: boolean;
  link_url?: string;
  link_label?: string;
}

export interface SurveyTargeting {
  url_match?: string;
  feature_flag?: string;
  // Tier 4 condition shape — discriminated by `type`.
  person_properties?: Array<Record<string, unknown>>;
  event_history?: {
    event_name: string;
    days?: number;
    count_op?: 'gt' | 'lt' | 'eq' | 'gte' | 'lte';
    count?: number;
  };
  frequency_cap?: { max_shows: number; days: number };
}

export interface SurveyDisplay {
  delay_ms?: number;
  position?: SurveyPosition;
  dismissable?: boolean;
}

export interface SurveyDefinition {
  id: string;
  name: string;
  type: SurveyType;
  active?: boolean;
  questions: SurveyQuestion[];
  targeting?: SurveyTargeting;
  display?: SurveyDisplay;
}

export interface SurveyAnswer {
  question_id: string;
  answer: string | number | boolean | string[] | null;
}

export interface SurveyResponse {
  answers: SurveyAnswer[];
  shown_at?: string;
  completed_at?: string;
}

export interface SurveyTargetingContext {
  url?: string;
  person_properties?: Record<string, unknown>;
  feature_flag_values?: Record<string, boolean | string>;
  event_counts?: Record<string, number>;
}
